import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import Flatpickr from 'react-flatpickr';
// eslint-disable-next-line import/no-unresolved
import 'flatpickr/dist/flatpickr.css';
import {
  ScreenLayout,
  FormField,
  FooterMeta,
  Spinner,
  LoadingOverlay,
  PageHeader,
  StyledButton,
} from '../shared';
import { isValidPhone } from '../../forms';
import { api } from '../../../../../shared/api';
import { ReactComponent as OpiniionLogo } from '../../../../assets/images/opiniion-logo.svg';
import { isDedicated } from '../../../../../shared/styles';
import { useBranding } from '../../../../context/BrandingContext';

const contactSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('You must enter your first name.')
    .trim('You must enter your first name.')
    .min(2, 'You must enter your first name.'),
  lastName: Yup.string()
    .required('You must enter your last name.')
    .trim('You must enter your last name.')
    .min(2, 'You must enter your last name.'),
  email: Yup.string()
    .email('You must enter a valid email.')
    .required('You must enter your email.'),
  moveInDate: Yup.string().required('You must select a date.'),
  phone: Yup.string().test(
    'is-valid-phone',
    'Enter a valid phone number.',
    (value) => (value ? isValidPhone(value) : true)
  ),
  message: Yup.string(),
});

type ContactFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  moveInDate: string;
  phone: string;
  message: string;
};

const initialValues: ContactFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  moveInDate: '',
  phone: '',
  message: '',
};

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledDescription = styled.p`
  margin: 0.75rem 0 1.25rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #57534e;
`;

const StyledFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledDateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  input {
    width: 100%;
    height: 48px;
    padding: 0.5rem;
    font-size: 1rem;
    font-family: inherit;
    color: #232323;
    border: 1px solid #d7d3d0;
    border-radius: 0.625rem;
    background: #ffffff;
    outline: none;
    transition: border-color 0.2s ease;
    box-sizing: border-box;

    &:focus {
      border-color: var(--rg-primary-color, #1249a1);
    }

    &::placeholder {
      color: #57534e;
      font-size: 0.9rem;
    }

    &:disabled {
      background: #f0f0f0;
      cursor: not-allowed;
    }
  }
`;

const StyledDateError = styled.span<{ $visible: boolean }>`
  font-size: 0.8125rem;
  color: #d32f2f;
  padding-left: 0.25rem;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  max-height: ${({ $visible }) => ($visible ? '2rem' : '0')};
  padding-bottom: ${({ $visible }) => ($visible ? '0.5rem' : '0')};
  overflow: hidden;
  transition:
    opacity 200ms ease,
    max-height 200ms ease,
    padding-bottom 200ms ease;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-family: inherit;
  color: #232323;
  border: 1px solid #d7d3d0;
  border-radius: 0.625rem;
  background: #ffffff;
  outline: none;
  resize: vertical;
  min-height: 5rem;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--rg-primary-color, #1249a1);
  }

  &::placeholder {
    color: #57534e;
    font-size: 0.9rem;
  }

  &:disabled {
    background: #f0f0f0;
    cursor: not-allowed;
  }
`;

const StyledLegal = styled.p`
  margin: 1rem 0 0.75rem;
  font-size: 0.6875rem;
  color: #57534e;

  a {
    color: #57534e;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const StyledSubmitButton = styled(StyledButton)`
  margin-top: 1rem;
`;

const StyledServerError = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: #a62017;
  text-align: center;
`;

const StyledFooter = styled.div`
  padding-top: 1rem;
  border-top: 1px solid #e8e9eb;
`;

const StyledSuccessBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledSuccessBox = styled.div`
  background: #fefce8;
  border: 2px solid #ffdf8f;
  border-radius: 0.5rem;
  padding: 1.25rem 1rem 1.5rem;
`;

const StyledSuccessHeader = styled.h3`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  color: #78350f;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const StyledSuccessText = styled.p`
  margin: 0.875rem 0 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #232323;
`;

const StyledInfoList = styled.ul`
  margin: 0.875rem 0 0;
  padding: 0;
  list-style: none;

  li {
    font-size: 0.875rem;
    line-height: 1.6;
    color: #232323;
  }
`;

const StyledGoBack = styled.p`
  margin: 1.5rem 0 0;
  text-align: center;

  button {
    font-family: inherit;
    font-size: 0.9375rem;
    color: #57534e;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;

    &:hover {
      color: #232323;
    }
  }
`;

const StyledLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;

  svg,
  img {
    max-height: 2.25rem;
    max-width: 8rem;
    object-fit: contain;
  }
`;

const DEFAULT_MESSAGE = "Hello! I'd love to learn more about this community.";

function ContactFields() {
  const {
    values,
    errors,
    touched,
    status,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    submitForm,
  } = useFormikContext<ContactFormValues>();

  const dateRef = useRef<Flatpickr>(null);
  const showDateError = touched.moveInDate && errors.moveInDate;

  return (
    <>
      <StyledFields>
        <FormField
          name="firstName"
          placeholder="First Name"
          value={values.firstName}
          error={errors.firstName}
          touched={touched.firstName}
          disabled={isSubmitting}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <FormField
          name="lastName"
          placeholder="Last Name"
          value={values.lastName}
          error={errors.lastName}
          touched={touched.lastName}
          disabled={isSubmitting}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <FormField
          name="email"
          type="email"
          placeholder="Email"
          value={values.email}
          error={errors.email}
          touched={touched.email}
          disabled={isSubmitting}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <StyledDateWrapper>
          <Flatpickr
            ref={dateRef}
            placeholder="Expected Move In Date"
            value={values.moveInDate}
            options={{
              dateFormat: 'm/d/Y',
              altFormat: 'm/d/Y',
              enableTime: false,
              altInput: true,
              position: 'auto',
              minDate: 'today',
              disableMobile: true,
            }}
            onChange={([date]) => {
              if (date) {
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                const y = date.getFullYear();
                setFieldValue('moveInDate', `${m}/${d}/${y}`);
              }
            }}
            onClose={() => setFieldTouched('moveInDate', true)}
          />
          <StyledDateError $visible={!!showDateError}>
            {errors.moveInDate || '\u00A0'}
          </StyledDateError>
        </StyledDateWrapper>
        <FormField
          name="phone"
          type="tel"
          placeholder="Phone (optional)"
          value={values.phone}
          error={errors.phone}
          touched={touched.phone}
          disabled={isSubmitting}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <StyledTextarea
          name="message"
          placeholder={DEFAULT_MESSAGE}
          value={values.message}
          disabled={isSubmitting}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </StyledFields>

      {status && <StyledServerError>{status}</StyledServerError>}

      <StyledSubmitButton
        type="button"
        disabled={!isValid || isSubmitting}
        onClick={submitForm}
      >
        {isSubmitting ? (
          <Spinner size="1.25rem" color="currentColor" />
        ) : (
          'Contact Property'
        )}
      </StyledSubmitButton>

      <StyledLegal>
        You agree to the Terms of Use & Privacy Policy. By choosing to
        contact a property, you also agree that Resident Ambassador, landlords,
        and property managers may call or text you about any inquiries you submit
        through our services, which may involve use of automated means and
        prerecorded/artificial voices. You don't need to consent as a condition
        of renting any property, or buying any other goods or services.
        Message/data rates may apply.{' '}
        <a
          href="https://opiniion.com/legal/terms-of-use/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Use
        </a>
        {' & '}
        <a
          href="https://opiniion.com/legal/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy.
        </a>
      </StyledLegal>
    </>
  );
}

function SuccessCheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0Zm4.53 7.97-5 5a.75.75 0 0 1-1.06 0l-2.5-2.5a.75.75 0 1 1 1.06-1.06L9 11.44l4.47-4.47a.75.75 0 1 1 1.06 1.06Z"
        fill="#78350f"
      />
    </svg>
  );
}

function SuccessView({
  listingName,
  values,
  onDone,
}: Readonly<{
  listingName: string;
  values: ContactFormValues;
  onDone: () => void;
}>) {
  const { logoUrl, hasCustomBranding } = useBranding();

  return (
    <StyledSuccessBody>
      <StyledSuccessBox>
        <StyledSuccessHeader>
          <SuccessCheckIcon />
          <span>
            <strong>Success!</strong> You&apos;ve contacted {listingName}
          </span>
        </StyledSuccessHeader>

        <StyledSuccessText>
          Here&apos;s a copy of the information you sent:
        </StyledSuccessText>

        <StyledInfoList>
          {values.firstName && (
            <li>
              <strong>First Name: </strong>
              {values.firstName}
            </li>
          )}
          {values.lastName && (
            <li>
              <strong>Last Name: </strong>
              {values.lastName}
            </li>
          )}
          {values.email && (
            <li>
              <strong>Email: </strong>
              {values.email}
            </li>
          )}
          {values.phone && (
            <li>
              <strong>Phone Number: </strong>
              {values.phone}
            </li>
          )}
          {values.moveInDate && (
            <li>
              <strong>Expected Move In: </strong>
              {values.moveInDate}
            </li>
          )}
          {(values.message || DEFAULT_MESSAGE) && (
            <li>
              <strong>Message: </strong>
              {values.message || DEFAULT_MESSAGE}
            </li>
          )}
        </StyledInfoList>

        <StyledSuccessText>
          The leasing team at {listingName} should contact you shortly.
        </StyledSuccessText>

        <StyledSuccessText>Thanks for using Resident Ambassador!</StyledSuccessText>
      </StyledSuccessBox>

      <StyledGoBack>
        <button type="button" onClick={onDone}>
          Go back
        </button>
      </StyledGoBack>

      <StyledLogoWrapper>
        {hasCustomBranding ? (
          <img src={logoUrl} alt="Property logo" />
        ) : (
          <OpiniionLogo />
        )}
      </StyledLogoWrapper>
    </StyledSuccessBody>
  );
}

export function ContactPage() {
  const navigate = useNavigate();
  const listing = globalThis.xprops.listing;
  const [submittedValues, setSubmittedValues] =
    useState<ContactFormValues | null>(null);

  const allowClose = useMemo(
    () => !isDedicated(globalThis.xprops.renderAs),
    []
  );

  const contactProperty = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      return api.post(`/contact_property/listing/${listing.id}`, {
        first_name: values.firstName,
        last_name: values.lastName,
        current_user_email: values.email,
        phone_number: values.phone,
        move_in_date: values.moveInDate,
        message: values.message || DEFAULT_MESSAGE,
        widget_rendered_as: globalThis.xprops.renderAs,
        via_widget: true,
      });
    },
  });

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleClose = useCallback(() => {
    globalThis.xprops?.onToggleMessenger?.();
  }, []);

  const handleSubmit = useCallback(
    async (values: ContactFormValues, helpers: any) => {
      try {
        await contactProperty.mutateAsync(values);
        setSubmittedValues(values);
      } catch {
        helpers.setStatus('Something went wrong. Please try again.');
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [contactProperty]
  );

  return (
    <ScreenLayout
      header={
        submittedValues ? undefined : (
          <PageHeader
            title={
              <>
                <PageHeader.Title>Contact</PageHeader.Title>
                <PageHeader.Title>{listing.name}</PageHeader.Title>
              </>
            }
            onBack={handleBack}
            onClose={allowClose ? handleClose : undefined}
          >
            <StyledDescription>
              To learn more about pricing or availability, or if you have other
              lease related questions, please contact the property team by
              filling out the form below.
            </StyledDescription>
          </PageHeader>
        )
      }
      footer={
        <StyledFooter>
          <FooterMeta />
        </StyledFooter>
      }
    >
      {contactProperty.isLoading && <LoadingOverlay />}
      {submittedValues ? (
        <SuccessView
          listingName={listing.name}
          values={submittedValues}
          onDone={() => navigate('/')}
        />
      ) : (
        <Formik<ContactFormValues>
          initialValues={initialValues}
          validationSchema={contactSchema}
          validateOnMount
          onSubmit={handleSubmit}
        >
          <StyledBody>
            <ContactFields />
          </StyledBody>
        </Formik>
      )}
    </ScreenLayout>
  );
}
